import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Building2,
  LayoutDashboard,
  Users,
  CreditCard,
  FolderTree,
  UserCog,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Globe,
  Settings,
  Handshake,
  Eye,
  XCircle,
  Key,
  Loader2,
  User,
  Mail,
  AlertCircle,
  ListTodo
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { API } from '../utils/api';
import { APP_VERSION } from '../utils/version';
import { WhatsNewDialog } from './WhatsNewDialog';

const baseNavItems = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard, roles: ['superadmin', 'admin', 'manager', 'professional', 'viewer'] },
  { path: '/investors', label: 'Partners', icon: Users, roles: ['superadmin', 'admin', 'manager', 'professional', 'viewer'] },
  { path: '/vendors', label: 'Vendors', icon: Handshake, roles: ['superadmin', 'admin', 'manager', 'professional'] },
  { path: '/checkbook', label: 'Checkbook', icon: CreditCard, roles: ['superadmin', 'admin', 'manager', 'professional'] },
  { path: '/categories', label: 'Categories', icon: FolderTree, roles: ['superadmin', 'admin', 'manager', 'professional'] },
];

const Layout = () => {
  const { user, logout, getAuthHeaders } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isEmulating, setIsEmulating] = useState(false);
  const [emulatingAs, setEmulatingAs] = useState('');
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const [seenVersion, setSeenVersion] = useState(null);
  const hasUnseenVersion = !!user && (seenVersion ?? user.last_seen_version) !== APP_VERSION;

  const openWhatsNew = () => {
    setWhatsNewOpen(true);
    if (!hasUnseenVersion || isEmulating) return;
    setSeenVersion(APP_VERSION);
    axios.post(API + '/auth/seen-version', { version: APP_VERSION }, { headers: getAuthHeaders() })
      .catch((err) => console.warn('Failed to save seen version', err));
  };
  const [tasksEnabled, setTasksEnabled] = useState(false);
  const [openTaskCount, setOpenTaskCount] = useState(0);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [appSettings, setAppSettings] = useState({
    app_name: 'Asset Commander',
    app_byline: 'LLC Management',
    footer_text: 'Asset Commander',
    footer_copyright: 'Global LLC Management',
    app_version: '1.0.0',
    password_reset_days: 90,
    password_expiry_enabled: false
  });
  const [passwordExpiryWarning, setPasswordExpiryWarning] = useState(null);
  const [forcedChangeDone, setForcedChangeDone] = useState(false);
  const mustChangePassword = !!user?.must_change_password && !forcedChangeDone && !isEmulating;

  // Optional periodic password expiry (off by default; Superadmin is exempt)
  useEffect(() => {
    if (user?.role === 'superadmin' || !appSettings.password_expiry_enabled) {
      setPasswordExpiryWarning(null);
      return;
    }
    if (user?.password_changed_at && appSettings.password_reset_days > 0) {
      const lastChanged = new Date(user.password_changed_at);
      const expiryDate = new Date(lastChanged);
      expiryDate.setDate(expiryDate.getDate() + appSettings.password_reset_days);
      
      const now = new Date();
      const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
      
      if (daysUntilExpiry <= 5 && daysUntilExpiry > 0) {
        setPasswordExpiryWarning({
          days: daysUntilExpiry,
          urgent: daysUntilExpiry <= 3
        });
      } else if (daysUntilExpiry <= 0) {
        setPasswordExpiryWarning({
          days: 0,
          expired: true
        });
      } else {
        setPasswordExpiryWarning(null);
      }
    } else {
      setPasswordExpiryWarning(null);
    }
  }, [user?.role, user?.password_changed_at, appSettings.password_reset_days, appSettings.password_expiry_enabled]);

  // Check if currently emulating
  useEffect(() => {
    const emulatingName = localStorage.getItem('emulating_as');
    const originalToken = localStorage.getItem('emulation_original_token');
    if (emulatingName && originalToken) {
      setIsEmulating(true);
      setEmulatingAs(emulatingName);
    } else {
      setIsEmulating(false);
      setEmulatingAs('');
    }
  }, []);

  // Exit emulation and return to admin
  const handleExitEmulation = () => {
    const originalToken = localStorage.getItem('emulation_original_token');
    if (originalToken) {
      // Restore original admin session
      localStorage.setItem('token', originalToken);
      localStorage.removeItem('emulation_original_token');
      localStorage.removeItem('emulation_original_user');
      localStorage.removeItem('emulating_as');
      toast.success('Returned to admin view');
      window.location.href = '/users';
    }
  };

  // Filter nav items based on user role
  const navItems = [
    ...baseNavItems.filter(item => item.roles.includes(user?.role || 'viewer')),
    // Add Tasks nav item if user has tasks enabled
    ...(tasksEnabled ? [{ path: '/tasks', label: 'Tasks', icon: ListTodo, roles: ['superadmin', 'admin', 'manager', 'professional', 'viewer'] }] : [])
  ];

  // Set tasks_enabled from user object (comes from /auth/me)
  useEffect(() => {
    if (user?.tasks_enabled !== undefined) {
      setTasksEnabled(user.tasks_enabled);
    }
  }, [user?.tasks_enabled]);

  // Fetch open task count when tasks are enabled
  useEffect(() => {
    if (tasksEnabled && user) {
      const headers = getAuthHeaders();
      axios.get(API + '/tasks', { headers })
        .then(res => {
          // Count tasks that are not completed
          const openCount = res.data.filter(task => !task.date_completed).length;
          setOpenTaskCount(openCount);
        })
        .catch(() => {
          setOpenTaskCount(0);
        });
    }
  }, [tasksEnabled, user, location.pathname]); // Refresh when navigating

  useEffect(() => {
    // Fetch public settings for header/footer
    axios.get(API + '/settings/public')
      .then(res => {
        setAppSettings(res.data);
      })
      .catch(() => {
        // Use defaults if fetch fails
      });
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordForm.newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    
    setPasswordLoading(true);
    try {
      await axios.post(API + '/auth/change-password', {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      }, { headers: getAuthHeaders() });
      
      toast.success('Password changed successfully');
      setForcedChangeDone(true);
      setProfileDialogOpen(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen flex flex-col ledger-background">
      {/* Header */}
      <header className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white sticky top-0 z-50 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 sm:gap-3 group" data-testid="logo-link">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-lg group-hover:shadow-amber-500/30 transition-shadow">
                <Globe className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-serif text-base sm:text-lg font-semibold tracking-tight">{appSettings.app_name}</h1>
                <p className="text-[10px] sm:text-xs text-amber-400/80 -mt-0.5 hidden sm:block">{appSettings.app_byline}</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isTasksItem = item.path === '/tasks';
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                      isActive(item.path)
                        ? 'bg-slate-800 text-white'
                        : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                    {isTasksItem && openTaskCount > 0 && (
                      <span className="ml-1 px-1.5 py-0.5 text-xs font-bold bg-amber-500 text-white rounded-full min-w-[18px] text-center">
                        {openTaskCount > 99 ? '99+' : openTaskCount}
                      </span>
                    )}
                  </Link>
                );
              })}
              {user?.role === 'superadmin' && (
                <Link
                  to="/users"
                  data-testid="nav-users"
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                    isActive('/users')
                      ? 'bg-slate-800 text-white'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <UserCog className="w-4 h-4" />
                  Users
                </Link>
              )}
              {user?.role === 'superadmin' && (
                <Link
                  to="/setup"
                  data-testid="nav-setup"
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                    isActive('/setup')
                      ? 'bg-slate-800 text-white'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <Settings className="w-4 h-4" />
                  Setup
                </Link>
              )}
            </nav>

            {/* User Menu */}
            <div className="flex items-center gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="text-white hover:bg-slate-800"
                    data-testid="user-menu-trigger"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-amber-700 flex items-center justify-center text-sm font-medium">
                        {user?.name?.charAt(0).toUpperCase()}
                      </div>
                      <span className="hidden sm:inline text-sm">{user?.name}</span>
                      <ChevronDown className="w-4 h-4" />
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                    <p className="text-xs text-amber-700 capitalize mt-1">{user?.role}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setProfileDialogOpen(true)} data-testid="profile-btn">
                    <User className="w-4 h-4 mr-2" />
                    My Profile
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {isEmulating ? (
                    <DropdownMenuItem onClick={handleExitEmulation} data-testid="exit-emulation-dropdown-btn" className="text-amber-700 focus:text-amber-800 focus:bg-amber-50">
                      <LogOut className="w-4 h-4 mr-2" />
                      Exit Emulation
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign out
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Mobile menu button */}
              <button
                className="md:hidden p-2"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-800">
            <nav className="px-4 py-3 space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isTasksItem = item.path === '/tasks';
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                      isActive(item.path)
                        ? 'bg-slate-800 text-white'
                        : 'text-slate-300 hover:bg-slate-800'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                    {isTasksItem && openTaskCount > 0 && (
                      <span className="ml-1 px-1.5 py-0.5 text-xs font-bold bg-amber-500 text-white rounded-full min-w-[18px] text-center">
                        {openTaskCount > 99 ? '99+' : openTaskCount}
                      </span>
                    )}
                  </Link>
                );
              })}
              {user?.role === 'superadmin' && (
                <Link
                  to="/users"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                    isActive('/users')
                      ? 'bg-slate-800 text-white'
                      : 'text-slate-300 hover:bg-slate-800'
                  }`}
                >
                  <UserCog className="w-4 h-4" />
                  Users
                </Link>
              )}
              {user?.role === 'superadmin' && (
                <Link
                  to="/setup"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                    isActive('/setup')
                      ? 'bg-slate-800 text-white'
                      : 'text-slate-300 hover:bg-slate-800'
                  }`}
                >
                  <Settings className="w-4 h-4" />
                  Setup
                </Link>
              )}
            </nav>
          </div>
        )}
      </header>

      {/* Emulation Banner */}
      {isEmulating && (
        <div className="bg-purple-600 text-white px-4 py-2 sticky top-16 z-40 print:hidden" data-testid="emulation-banner">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              <span className="text-sm font-medium">
                Viewing as: <strong>{emulatingAs}</strong>
              </span>
            </div>
            <Button
              size="sm"
              variant="secondary"
              onClick={handleExitEmulation}
              className="bg-white text-purple-700 hover:bg-purple-100 h-7 px-3"
              data-testid="exit-emulation-btn"
            >
              <XCircle className="w-4 h-4 mr-1" />
              Exit Emulation
            </Button>
          </div>
        </div>
      )}

      {/* Password Expiration Warning Banner */}
      {passwordExpiryWarning && (
        <div 
          className={`px-4 py-2 sticky ${isEmulating ? 'top-24' : 'top-16'} z-40 print:hidden ${
            passwordExpiryWarning.expired 
              ? 'bg-rose-600 text-white' 
              : passwordExpiryWarning.urgent 
                ? 'bg-amber-500 text-white' 
                : 'bg-amber-100 text-amber-900 border-b border-amber-200'
          }`}
          data-testid="password-expiry-banner"
        >
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm font-medium">
                {passwordExpiryWarning.expired ? (
                  <>Your password has expired. Please change it now.</>
                ) : passwordExpiryWarning.days === 1 ? (
                  <>Your password expires tomorrow. Please change it soon.</>
                ) : (
                  <>Your password expires in <strong>{passwordExpiryWarning.days} days</strong>. Please change it soon.</>
                )}
              </span>
            </div>
            <Button
              size="sm"
              variant={passwordExpiryWarning.expired || passwordExpiryWarning.urgent ? "secondary" : "outline"}
              onClick={() => setProfileDialogOpen(true)}
              className={`h-7 px-3 ${
                passwordExpiryWarning.expired 
                  ? 'bg-white text-rose-700 hover:bg-rose-100' 
                  : passwordExpiryWarning.urgent 
                    ? 'bg-white text-amber-700 hover:bg-amber-100' 
                    : ''
              }`}
              data-testid="change-password-btn"
            >
              <Key className="w-4 h-4 mr-1" />
              Change Password
            </Button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-1 w-full">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 border-t border-slate-600 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
            <p className="text-sm text-slate-300">
              {appSettings.footer_text} &copy; {new Date().getFullYear()} — {appSettings.footer_copyright}
            </p>
            <button
              type="button"
              onClick={openWhatsNew}
              className="relative inline-flex items-center gap-1.5 text-xs text-amber-400/70 hover:text-amber-300 underline-offset-2 hover:underline transition-colors"
              data-testid="footer-version-btn"
            >
              {hasUnseenVersion && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-900 no-underline" data-testid="footer-version-new-dot">
                  <span className="h-1.5 w-1.5 rounded-full bg-slate-900 animate-pulse" />
                  New
                </span>
              )}
              Version {APP_VERSION}
            </button>
          </div>
        </div>
      </footer>
      <WhatsNewDialog open={whatsNewOpen} onOpenChange={setWhatsNewOpen} />

      {/* Profile Dialog */}
      <Dialog open={profileDialogOpen || mustChangePassword} onOpenChange={(open) => { if (!mustChangePassword) setProfileDialogOpen(open); }}>
        <DialogContent
          className={`sm:max-w-[425px] ${mustChangePassword ? '[&>button]:hidden' : ''}`}
          onInteractOutside={(e) => { if (mustChangePassword) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (mustChangePassword) e.preventDefault(); }}
          data-testid="profile-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              {mustChangePassword ? 'Choose Your Password' : 'My Profile'}
            </DialogTitle>
            <DialogDescription>
              {mustChangePassword ? 'Your password was set by an administrator.' : 'Manage your account settings'}
            </DialogDescription>
          </DialogHeader>
          {mustChangePassword && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm" data-testid="must-change-password-notice">
              <AlertCircle className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
              <p className="text-amber-800">
                Please replace the temporary password with one of your own. Enter the temporary password as your current password.
              </p>
            </div>
          )}
          
          <div className="py-4 space-y-6">
            {/* User Info Section */}
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="w-12 h-12 rounded-full bg-amber-700 flex items-center justify-center text-white text-lg font-medium">
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-sm text-slate-500">{user?.email}</p>
                  <p className="text-xs text-amber-700 capitalize">{user?.role}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg text-sm">
                <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-blue-700">
                  To change your email address, please contact your administrator.
                </p>
              </div>
            </div>
            
            {/* Change Password Section */}
            <div className="border-t pt-4">
              <h4 className="font-medium flex items-center gap-2 mb-4">
                <Key className="w-4 h-4" />
                Change Password
              </h4>
              <form onSubmit={handleChangePassword} className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Current Password</Label>
                  <Input
                    id="currentPassword"
                    data-testid="current-password-input"
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                    placeholder="Enter current password"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <Input
                    id="newPassword"
                    data-testid="new-password-input"
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    placeholder="Enter new password (min 8 characters)"
                    required
                    minLength={8}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <Input
                    id="confirmPassword"
                    data-testid="confirm-password-input"
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                    placeholder="Confirm new password"
                    required
                  />
                </div>
                <Button type="submit" className="w-full bg-amber-700 hover:bg-amber-800" disabled={passwordLoading} data-testid="change-password-submit">
                  {passwordLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Key className="w-4 h-4 mr-2" />}
                  Change Password
                </Button>
              </form>
            </div>
          </div>
          
          <DialogFooter>
            {mustChangePassword ? (
              <Button variant="outline" onClick={handleLogout} data-testid="must-change-logout-btn">
                Log Out
              </Button>
            ) : (
              <Button variant="outline" onClick={() => setProfileDialogOpen(false)} data-testid="profile-close-btn">
                Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Layout;
